import { MoneyFormat } from "~/components/Money";
import { formatMoney } from "~/components/Money/utils";
import { usdDollarsToMoney } from "./moneyUtils";
import type { ProposedChangeMetadata, StoredProposedChange } from "../types";

export const EUM_VALUE_OVERRIDE_ROW_LABEL = "EUM value override";
export const EUM_CALCULATION_VALUES_ROW_LABEL = "EUM calculation values";

const MISSING_VALUE_PLACEHOLDER = "--";

export const formatEumDollars = (dollars: number | null): string => {
  const money = usdDollarsToMoney(dollars);
  return money ? formatMoney(money, MoneyFormat.Symbol, true) : MISSING_VALUE_PLACEHOLDER;
};

export type EumValueOverrideChangeInput = {
  changeRequestId: string;
  entityId: number;
  entityName: string;
  currentEumValue: number | null;
  proposedEumValue: number;
  metadata?: ProposedChangeMetadata;
};

export const buildEumValueOverrideChange = ({
  changeRequestId,
  entityId,
  entityName,
  currentEumValue,
  proposedEumValue,
  metadata,
}: EumValueOverrideChangeInput): StoredProposedChange => ({
  changeRequestId,
  billingGroupName: entityName,
  entityIds: [entityId],
  detail: {
    label: EUM_VALUE_OVERRIDE_ROW_LABEL,
    currentValues: [`EUM: ${formatEumDollars(currentEumValue)}`],
    proposedValues: [`EUM: ${formatEumDollars(proposedEumValue)}`],
  },
  ...(metadata && { metadata }),
});

export type EumCalcEditValues = {
  eumValue: number | null;
  netCapitalValue: number | null;
  totalCommitmentValue: number | null;
  distributionsValue: number | null;
};

export type EumCalcEditChangeInput = {
  changeRequestId: string;
  entityId: number;
  entityName: string;
  current: EumCalcEditValues;
  proposed: EumCalcEditValues;
  metadata?: ProposedChangeMetadata;
};

const buildCalcEditValueLines = (values: EumCalcEditValues): string[] => [
  `EUM: ${formatEumDollars(values.eumValue)}`,
  `Net capital value: ${formatEumDollars(values.netCapitalValue)}`,
  `Total commitment value: ${formatEumDollars(values.totalCommitmentValue)}`,
  `Distributions value: ${formatEumDollars(values.distributionsValue)}`,
];

export const buildEumCalcEditChange = ({
  changeRequestId,
  entityId,
  entityName,
  current,
  proposed,
  metadata,
}: EumCalcEditChangeInput): StoredProposedChange => ({
  changeRequestId,
  billingGroupName: entityName,
  entityIds: [entityId],
  detail: {
    label: EUM_CALCULATION_VALUES_ROW_LABEL,
    currentValues: buildCalcEditValueLines(current),
    proposedValues: buildCalcEditValueLines(proposed),
  },
  ...(metadata && { metadata }),
});
